import clsx from "clsx";
import { type ChangeEvent, type FocusEvent, type ReactNode, createContext, useContext, useId } from "react";
import type { InputProps } from "../../../types/FormTypes";
import radioButtonStyles from "../RadioButtons/RadioButtons.module.scss";
import styles from "../RadioCards/RadioCards.module.scss";

export interface RadioGroupContextValue {
  disabled: boolean;
  error?: boolean;
  name: string;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onValueChange?: (value: string) => void;
  readOnly: boolean;
  required: boolean;
  value?: string;
}

const RadioGroupContext = createContext<RadioGroupContextValue | undefined>(undefined);

export const useRadioGroup = () => useContext(RadioGroupContext);

export interface RadioGroupProps<TValue extends string = string> extends InputProps<TValue> {
  ariaLabel: string;
  children: ReactNode;
  name?: string;
  columns?: 1 | 2 | 3;
  required?: boolean;
  variant?: "default" | "buttons";
  /** Button size for `variant="buttons"`. */
  size?: "sm" | "md";
}

const RadioGroup = <TValue extends string = string>({
  ariaLabel,
  children,
  value,
  onValueChange,
  name,
  columns = 1,
  disabled = false,
  readOnly = false,
  required = false,
  className,
  onChange,
  onBlur,
  onFocus,
  error,
  fullWidth,
  variant = "default",
  size = "md",
}: RadioGroupProps<TValue>) => {
  const generatedName = useId();

  return (
    <fieldset
      className={clsx(styles.root, variant === "buttons" && radioButtonStyles.radioButtonGroup, className)}
      data-columns={columns}
      data-size={variant === "buttons" ? size : undefined}
      data-error={error || undefined}
      data-full-width={fullWidth || undefined}
      disabled={disabled}
    >
      <legend className={styles.legend}>{ariaLabel}</legend>
      <RadioGroupContext.Provider
        value={{
          disabled,
          error,
          name: name ?? generatedName,
          onBlur,
          onChange: onChange as ((event: ChangeEvent<HTMLInputElement>) => void) | undefined,
          onFocus,
          onValueChange: onValueChange as ((value: string) => void) | undefined,
          readOnly,
          required,
          value,
        }}
      >
        {children}
      </RadioGroupContext.Provider>
    </fieldset>
  );
};

export default RadioGroup;
